import { useState, useEffect } from 'react';
import { api } from '../../api';
import type { InterrogationResultRow, InterrogationVerdict } from '../../types';

interface Props {
  domainId: string;
  domain: string;
}

// ── Display config ─────────────────────────────────────────────────────────

const MODEL_LABELS: Record<string, string> = {
  chatgpt:    'ChatGPT',
  claude:     'Claude',
  perplexity: 'Perplexity',
  gemini:     'Gemini',
  mistral:    'Mistral',
  deepseek:   'DeepSeek',
};

const MODEL_DOT_COLOR: Record<string, string> = {
  chatgpt:    'bg-emerald-500',
  claude:     'bg-violet-500',
  perplexity: 'bg-sky-500',
  gemini:     'bg-amber-500',
  mistral:    'bg-indigo-500',
  deepseek:   'bg-rose-500',
};

const VERDICT_CHIP: Record<InterrogationVerdict, string> = {
  'Accurate':     'bg-emerald-50 text-emerald-700 border border-emerald-200',
  'Outdated':     'bg-amber-50  text-amber-700  border border-amber-200',
  'Generic':      'bg-amber-50  text-amber-700  border border-amber-200',
  'Wrong source': 'bg-red-50    text-red-700    border border-red-200',
  'Unscored':     'bg-gray-50   text-gray-500   border border-gray-200',
};

const CATEGORY_LABELS: Record<string, string> = {
  icp:         'Ideal customer',
  positioning: 'Positioning',
  strengths:   'Strengths',
  competitor:  'Competitor',
};

// ── Helpers ────────────────────────────────────────────────────────────────

/** Truncate response text for display — show first 160 chars */
function excerpt(text: string, maxLen = 160): string {
  if (!text) return '—';
  const trimmed = text.trim();
  if (trimmed.length <= maxLen) return trimmed;
  return trimmed.slice(0, maxLen).trimEnd() + '…';
}

/** Group results by question_id, preserving question text + category */
function groupByQuestion(results: InterrogationResultRow[]): Array<{
  questionId: string;
  question: string;
  category: string;
  rows: InterrogationResultRow[];
}> {
  const order: string[] = [];
  const map = new Map<string, { question: string; category: string; rows: InterrogationResultRow[] }>();
  for (const r of results) {
    if (!map.has(r.questionId)) {
      order.push(r.questionId);
      map.set(r.questionId, { question: r.question, category: r.questionCategory, rows: [] });
    }
    map.get(r.questionId)!.rows.push(r);
  }
  return order.map((qid) => ({ questionId: qid, ...map.get(qid)! }));
}

// ── Component ──────────────────────────────────────────────────────────────

export function AiInterrogationTab({ domainId }: Props) {
  const [data, setData] = useState<import('../../types').InterrogationResultsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.domains
      .getInterrogationResults(domainId)
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [domainId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-10 text-center text-sm text-red-500">{error}</div>
    );
  }

  if (!data || data.results.length === 0) {
    return (
      <div className="py-20 text-center">
        <p className="text-gray-400 text-sm">No AI interrogation data yet.</p>
        <p className="text-gray-400 text-xs mt-1">
          Your first sweep runs automatically once your domain is verified and business context is confirmed.
        </p>
      </div>
    );
  }

  const questions = groupByQuestion(data.results);
  const modelCount = new Set(data.results.map((r) => r.model)).size;

  // Count fixes needed
  const fixRows = data.results.filter((r) =>
    r.verdict === 'Outdated' || r.verdict === 'Generic' || r.verdict === 'Wrong source',
  );
  const fixHints = [...new Set(fixRows.map((r) => r.fixHint).filter(Boolean))] as string[];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold text-brand-ink">AI interrogation</h2>
        <span className="text-xs bg-gray-100 text-gray-500 px-2.5 py-0.5 rounded-full font-medium">
          {modelCount} model{modelCount !== 1 ? 's' : ''}
        </span>
        <span className="text-xs text-gray-400 ml-auto">
          Sampled {data.createdAt ? new Date(data.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
        </span>
      </div>

      {/* Questions */}
      {questions.map((q, qi) => (
        <div key={q.questionId} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {/* Question header */}
          <div className="px-5 py-4 border-b border-gray-50">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-medium text-brand-ink leading-snug">{q.question}</p>
              <span className="text-[11px] whitespace-nowrap text-gray-400 mt-0.5 shrink-0">
                Question {qi + 1} of {questions.length}
                {q.category ? ` · ${CATEGORY_LABELS[q.category] ?? q.category}` : ''}
                {' · sampled monthly'}
              </span>
            </div>
          </div>

          {/* Per-model rows */}
          <div className="divide-y divide-gray-50">
            {q.rows.map((r) => (
              <div key={r.id} className="px-5 py-3.5 flex items-start gap-4">
                {/* Model pill */}
                <div className="flex items-center gap-1.5 min-w-[100px] pt-0.5">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${MODEL_DOT_COLOR[r.model] ?? 'bg-gray-300'}`} />
                  <span className="text-xs font-medium text-gray-600">
                    {MODEL_LABELS[r.model] ?? r.model}
                  </span>
                </div>
                {/* Response excerpt */}
                <p className="flex-1 text-xs text-gray-500 leading-relaxed">
                  {excerpt(r.rawResponse)}
                </p>
                {/* Verdict chip */}
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap shrink-0 ${VERDICT_CHIP[r.verdict] ?? VERDICT_CHIP['Unscored']}`}>
                  {r.verdict}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Fix callout */}
      {fixHints.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
          <p className="text-sm font-semibold text-amber-800 mb-2">
            {fixHints.length} fix{fixHints.length !== 1 ? 'es' : ''} drafted.
          </p>
          <ul className="space-y-1.5 mb-4">
            {fixHints.slice(0, 5).map((hint, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-amber-700">
                <span className="mt-0.5 shrink-0">›</span>
                <span>{hint}</span>
              </li>
            ))}
            {fixHints.length > 5 && (
              <li className="text-xs text-amber-500 pl-4">+{fixHints.length - 5} more</li>
            )}
          </ul>
          <a
            href="#suggestions"
            className="inline-flex items-center text-xs font-medium text-amber-700 hover:text-amber-900 transition-colors"
          >
            Review fixes →
          </a>
        </div>
      )}
    </div>
  );
}
